/**
 * UnsavedChangesDialog — shown when the user tries to navigate away
 * from a page with unsaved changes.
 */

import { AlertTriangle } from 'lucide-react';

interface UnsavedChangesDialogProps {
  open:       boolean;
  onDiscard:  () => void; // Leave without saving
  onStay:     () => void; // Stay and keep editing
}

export default function UnsavedChangesDialog({
  open,
  onDiscard,
  onStay,
}: UnsavedChangesDialogProps) {
  if (!open) return null;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onStay}
    >
      {/* Panel */}
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Top colour bar */}
        <div className="h-1 bg-amber-400" />

        <div className="p-6 space-y-5">
          {/* Icon + heading */}
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center
                            rounded-full bg-amber-50 border border-amber-200">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-surface-900">
                Unsaved changes
              </h2>
              <p className="text-sm text-surface-500 mt-1">
                You have unsaved changes on this day. If you leave now they'll be lost.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              onClick={onDiscard}
              className="flex-1 rounded-xl border border-surface-200 px-4 py-2.5 text-sm
                         font-semibold text-surface-700 hover:bg-surface-50
                         transition-colors focus:outline-none focus:ring-2
                         focus:ring-surface-300"
            >
              Discard changes
            </button>
            <button
              onClick={onStay}
              autoFocus
              className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm
                         font-semibold text-white hover:bg-primary-700
                         transition-colors focus:outline-none focus:ring-2
                         focus:ring-primary-500 focus:ring-offset-2"
            >
              Keep editing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
